import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useUserCause, useUserData } from 'core/Api';
import { LearnMoreExtensionAccordion } from 'core/Components';
import { usePlatformService } from 'core/Hooks';
import { Box, ButtonMantine, Center, Image, LoadingOverlay, Modal, Stack, Text, rem } from 'ui';

import { useNativeInstallExtension } from '../../hooks/mobile/useNativeApp';
import useGtmServices from '../../hooks/useGtmServices';

type OpenDrawerState = boolean;
type ControlDrawer = (boolean: boolean) => void;
interface Props {
  drawerControl: [OpenDrawerState, ControlDrawer];
}

type onExtensionInstall = boolean;
type setIsExtensionInstalling = React.Dispatch<React.SetStateAction<boolean>>;
type failedToValidate = boolean;
type setFailedToValidate = React.Dispatch<React.SetStateAction<boolean>>;

interface ExtensionProps {
  extensionInstall: [onExtensionInstall, setIsExtensionInstalling];
  extensionValidation: [failedToValidate, setFailedToValidate];
  onSuccess: () => void;
}

const NativeExtensionContent = ({
  nativeStatus,
}: {
  nativeStatus: { isValidating: boolean; couldNotValidate: boolean };
}) => {
  const { isValidating, couldNotValidate } = nativeStatus;

  const { data: cause } = useUserCause();

  return (
    <Stack gap={8} c="#767676" style={{ overflow: 'scroll' }}>
      <Text fz={16} fw={700} lh="19px">
        Add the Givebacks mobile extension!
      </Text>
      <Text fz={14} fw={400} lh="17px">
        Enable the mobile browser extension to earn automatic donations for{' '}
        {`${cause?.defaulted ? 'your cause' : cause?.name}`} whenever you shop at a participating
        merchant website on your native mobile browser.
      </Text>
      <Stack justify="center" align={'center'} mt="md">
        <Center pt={'lg'} style={{ position: 'relative' }}>
          <Image
            src="https://s3.amazonaws.com/com.givebacks.public/images/onboarding/MobileExtension/extensionOnPhone_icon.png"
            style={{ height: 305 }}
          />
          <Box
            style={{ position: 'absolute' }}
            w={287}
            h={90}
            bg={
              'linear-gradient(0deg, #FFFFFF 0%, rgba(255, 255, 255, 0.927083) 21.87%, rgba(255, 255, 255, 0) 100%)'
            }
            bottom={0}
          ></Box>
        </Center>
        {isValidating && (
          <Stack justify="center" align={'center'} mt="md">
            {couldNotValidate ? (
              <Text fz={'md'}>
                We could not verify if the Extension has been installed, please try again.
              </Text>
            ) : (
              <>
                <Text fz={'md'}>Please wait as we verify the extension has been installed</Text>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

// Moved this out so that it is dismounted from the dom on close of the drawer
const NativeExtensionContainer = ({
  extensionInstall,
  onSuccess,
  extensionValidation,
}: ExtensionProps) => {
  const GtmService = useGtmServices();
  const Platform = usePlatformService();
  // Set Parent State. Can refactor to a small context. Would be way better.
  const [onExtensionInstall, setIsExtensionInstalling] = extensionInstall;
  const [failedToValidate, setFailedToValidate] = extensionValidation;
  const [showLoader, setShowLoader] = useState(false);

  const { refetch } = useUserData();

  const refetchExtensionStatus = useCallback(async () => {
    const { data: user } = await refetch();
    return user?.onboarding.installed_mobile;
  }, [refetch]);

  const handleSuccess = () => {
    GtmService.events.extensionInstalled(Platform.mobileBrowser == 'ios' ? 'ios' : 'android');
    onSuccess();
  };

  const checkUserExtension = async () => {
    const isExtensionInstalled = await refetchExtensionStatus();
    if (isExtensionInstalled) {
      setShowLoader(false);
      handleSuccess();
    } else if (couldNotValidate) {
      setFailedToValidate(true);
      setShowLoader(false);
    }
  };

  // When we get a success message, double check user for the updated status.
  const { installNativeExtension, isValidating, couldNotValidate } = useNativeInstallExtension({
    onSuccess: checkUserExtension,
    onFailure: checkUserExtension,
  });

  const buttonText = useMemo(() => {
    if (failedToValidate) return 'Skip';
    if (onExtensionInstall) return 'Learn More';
    return 'Back';
  }, [onExtensionInstall, failedToValidate]);

  const handleGetExtensionClick = async () => {
    setShowLoader(true);
    const isExtensionInstalled = await refetchExtensionStatus();
    if (isExtensionInstalled) {
      handleSuccess();
    } else {
      installNativeExtension();
    }
  };

  // When a user comes back to the app without a success message, double check to see if they passed extension status
  // This could happen if they came back to the app without hitting the last button
  useEffect(() => {
    isValidating && checkUserExtension();
  }, [couldNotValidate]);

  return (
    <Stack h="100%" justify="space-between">
      <Stack gap={0}>
        {onExtensionInstall ? (
          <NativeExtensionContent
            nativeStatus={{ isValidating, couldNotValidate: failedToValidate }}
          />
        ) : (
          <LearnMoreExtensionAccordion variant="separated" />
        )}
      </Stack>
      <Stack
        gap="md"
        py="xl"
        px="md"
        w="100%"
        style={{ position: 'absolute', bottom: 0, left: 0 }}
        bg="white"
      >
        <ButtonMantine
          onClick={() =>
            GtmService.click(handleGetExtensionClick, {
              type: 'button',
              action: 'install_extension',
              label: 'Get the Extension',
            })
          }
        >
          {failedToValidate ? 'Try Again' : 'Get the Extension'}
        </ButtonMantine>
        <ButtonMantine
          variant="subtle"
          onClick={() => {
            if (onExtensionInstall) {
              GtmService.click(() => setIsExtensionInstalling((prev) => !prev), {
                type: 'button',
                action: 'info',
                label: 'Learn More',
              });
            } else {
              setIsExtensionInstalling((prev) => !prev);
            }
          }}
        >
          {buttonText}
        </ButtonMantine>
      </Stack>
      <LoadingOverlay
        visible={showLoader}
        overlayProps={{ backgroundOpacity: 0.3 }}
        loaderProps={{ size: 92, color: 'cobalt.7', variant: 'dots' }}
      />
    </Stack>
  );
};

export const OnlineShoppingDrawer = ({ drawerControl }: Props) => {
  const [isOpen, setIsOpen] = drawerControl;

  const [onExtensionInstall, setIsExtensionInstalling] = useState(true);
  const [failedToValidate, setFailedToValidate] = useState(false);

  const handleClose = () => {
    setIsExtensionInstalling(true);
    setFailedToValidate(false);
    setIsOpen(false);
  };

  const modalTitle = useMemo(() => {
    if (failedToValidate) return 'Oops, Extension not activated correctly';
    if (onExtensionInstall) return 'Online Shopping Program';
    return 'Learn More';
  }, [onExtensionInstall, failedToValidate]);

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      closeOnClickOutside
      closeOnEscape
      title={modalTitle}
      variant="full-screen-mobile"
      styles={{ header: { paddingTop: rem(48) } }}
    >
      <NativeExtensionContainer
        extensionInstall={[onExtensionInstall, setIsExtensionInstalling]}
        extensionValidation={[failedToValidate, setFailedToValidate]}
        onSuccess={handleClose}
      />
    </Modal>
  );
};
